import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Play, Search, Bell, X, ChevronRight, Clock, Check, BarChart3 } from "lucide-react";
import { API, BACKEND_URL } from "../utils/api";
import { AnalyticsDashboard, YouTubeImporter, BulkEpisodeUpload, TranscodingManager } from "../components/admin";
import BroadcastOS from "../components/admin/BroadcastOS";
import { PushNotificationManager } from "../components/notifications";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [ppvShows, setPpvShows] = useState([]);
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [movies, setMovies] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [subscribers, setSubscribers] = useState({ subscribers: [], total: 0, by_preference: {} });
  const [activeTab, setActiveTab] = useState('overview');
  const [showModal, setShowModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedShow, setSelectedShow] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Token-based auth: prefer stored JWT, fallback to Basic Auth
  const adminToken = localStorage.getItem('adminToken');

  const authHeader = adminToken
    ? { headers: { Authorization: `Bearer ${adminToken}` } }
    : { auth: { username: localStorage.getItem('adminUser') || 'Incognitox', password: '5683Mask' } };

  const fetchAuthHeaders = adminToken
    ? { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' }
    : { 'Authorization': 'Basic ' + btoa((localStorage.getItem('adminUser') || 'Incognitox') + ':5683Mask'), 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ppvRes, channelsRes, catsRes, showsRes, moviesRes, schedulesRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, authHeader),
        axios.get(`${API}/admin/ppv-shows`, authHeader),
        axios.get(`${API}/admin/channels`, authHeader),
        axios.get(`${API}/admin/categories`, authHeader),
        axios.get(`${API}/admin/tv-shows`, authHeader),
        axios.get(`${API}/admin/movies`, authHeader),
        axios.get(`${API}/admin/schedule`, authHeader),
        axios.get(`${API}/admin/subscribers`, authHeader).catch(() => ({ data: { subscribers: [], total: 0, by_preference: {} } }))
      ]);
      setStats(statsRes.data.stats);
      setPpvShows(ppvRes.data);
      setChannels(channelsRes.data);
      setCategories(catsRes.data);
      setTvShows(showsRes.data);
      setMovies(moviesRes.data);
      setSchedules(schedulesRes.data);
      setSubscribers(subscribersRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin');
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  // PPV handlers
  const handleCreatePPV = async (data) => {
    try {
      await axios.post(`${API}/admin/ppv-shows`, data, authHeader);
      fetchData();
      setShowModal(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating show');
    }
  };

  const handleDeletePPV = async (id) => {
    if (!window.confirm('Are you sure you want to delete this show?')) return;
    try {
      await axios.delete(`${API}/admin/ppv-shows/${id}`, authHeader);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting show');
    }
  };

  // Channel handlers
  const handleCreateChannel = async (data) => {
    try {
      await axios.post(`${API}/admin/channels`, data, authHeader);
      fetchData();
      setShowModal(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating channel');
    }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) return;
    try {
      await axios.delete(`${API}/admin/channels/${id}`, authHeader);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting channel');
    }
  };

  // TV Show handlers
  const handleCreateTVShow = async (data) => {
    try {
      const params = new URLSearchParams(data);
      await axios.post(`${API}/admin/tv-shows?${params}`, {}, authHeader);
      fetchData();
      setShowModal(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating TV show');
    }
  };

  const handleDeleteTVShow = async (id) => {
    if (!window.confirm('Are you sure you want to delete this TV show?')) return;
    try {
      await axios.delete(`${API}/admin/tv-shows/${id}`, authHeader);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting TV show');
    }
  };

  // Episode handlers
  const handleAddEpisode = async (showId, data) => {
    try {
      const params = new URLSearchParams(data);
      await axios.post(`${API}/admin/tv-shows/${showId}/episodes?${params}`, {}, authHeader);
      fetchData();
      setShowModal(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error adding episode');
    }
  };

  const handleDeleteEpisode = async (showId, episodeId) => {
    if (!window.confirm('Delete this episode?')) return;
    try {
      await axios.delete(`${API}/admin/tv-shows/${showId}/episodes/${episodeId}`, authHeader);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting episode');
    }
  };

  // Movie handlers
  const handleCreateMovie = async (data) => {
    try {
      const params = new URLSearchParams(data);
      await axios.post(`${API}/admin/movies?${params}`, {}, authHeader);
      fetchData();
      setShowModal(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating movie');
    }
  };

  const handleDeleteMovie = async (id) => {
    if (!window.confirm('Are you sure you want to delete this movie?')) return;
    try {
      await axios.delete(`${API}/admin/movies/${id}`, authHeader);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting movie');
    }
  };

  // Schedule handlers
  const handleCreateSchedule = async (data) => {
    try {
      const params = new URLSearchParams(data);
      await axios.post(`${API}/admin/schedule?${params}`, {}, authHeader);
      fetchData();
      setShowModal(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating schedule entry');
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('Delete this schedule entry?')) return;
    try {
      await axios.delete(`${API}/admin/schedule/${id}`, authHeader);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting schedule');
    }
  };

  // Subscriber handlers
  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, authHeader);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error removing subscriber');
    }
  };

  const handleExportSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscribers/export`, authHeader);
      // Create download link
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cbxtv_subscribers.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting subscribers');
    }
  };

  // Get channel schedules
  const getChannelSchedules = (channelId) => {
    return schedules.filter(s => s.channel_id === channelId).sort((a, b) => 
      new Date(a.start_time) - new Date(b.start_time)
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_streamking-cbx/artifacts/exnqqglo_cbxtransp.png" 
                alt="CBXTV" 
                className="h-28 w-auto object-contain"
                style={{ filter: 'drop-shadow(0 0 3px white) drop-shadow(0 0 6px rgba(255,255,255,0.6))' }}
              />
              <span className="text-gray-400 text-lg">CMS</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-400">Welcome, {localStorage.getItem('adminUser') || 'Admin'}</span>
            <button onClick={handleLogout} className="text-red-500 hover:text-red-400">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-gray-900 min-h-[calc(100vh-73px)] border-r border-gray-800 p-4">
          <nav className="space-y-1">
            <p className="text-xs text-gray-500 uppercase tracking-wider px-4 py-2">Command Center</p>
            <AdminNavItem icon="📡" text="Network Command" active={activeTab === 'broadcast'} onClick={() => setActiveTab('broadcast')} />
            
            <p className="text-xs text-gray-500 uppercase tracking-wider px-4 py-2 mt-4">Dashboard</p>
            <AdminNavItem icon="📊" text="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
            
            <p className="text-xs text-gray-500 uppercase tracking-wider px-4 py-2 mt-4">Content</p>
            <AdminNavItem icon="🎬" text="PPV Shows" active={activeTab === 'ppv'} onClick={() => setActiveTab('ppv')} />
            <AdminNavItem icon="📺" text="TV Shows" active={activeTab === 'tvshows'} onClick={() => setActiveTab('tvshows')} />
            <AdminNavItem icon="🎥" text="Movies" active={activeTab === 'movies'} onClick={() => setActiveTab('movies')} />
            
            <p className="text-xs text-gray-500 uppercase tracking-wider px-4 py-2 mt-4">Tools</p>
            <AdminNavItem icon="▶️" text="YouTube Import" active={activeTab === 'youtube'} onClick={() => setActiveTab('youtube')} />
            <AdminNavItem icon="📤" text="Bulk Upload" active={activeTab === 'bulkupload'} onClick={() => setActiveTab('bulkupload')} />
            <AdminNavItem icon="⚙️" text="Transcoding" active={activeTab === 'transcoding'} onClick={() => setActiveTab('transcoding')} />
            
            <p className="text-xs text-gray-500 uppercase tracking-wider px-4 py-2 mt-4">Channels</p>
            <AdminNavItem icon="📡" text="All Channels" active={activeTab === 'channels'} onClick={() => setActiveTab('channels')} />
            <AdminNavItem icon="📅" text="Schedule" active={activeTab === 'schedule'} onClick={() => setActiveTab('schedule')} />
            <AdminNavItem icon="📁" text="Categories" active={activeTab === 'categories'} onClick={() => setActiveTab('categories')} />
            
            <p className="text-xs text-gray-500 uppercase tracking-wider px-4 py-2 mt-4">Users</p>
            <AdminNavItem icon="👥" text="Subscribers" active={activeTab === 'subscribers'} onClick={() => setActiveTab('subscribers')} />
            <AdminNavItem icon="📧" text="Email Notifications" active={activeTab === 'email'} onClick={() => setActiveTab('email')} />
            
            <p className="text-xs text-gray-500 uppercase tracking-wider px-4 py-2 mt-4">Monetization</p>
            <AdminNavItem icon="💳" text="Payments" active={activeTab === 'payments'} onClick={() => setActiveTab('payments')} />
            <AdminNavItem icon="🔔" text="Push Notifications" active={activeTab === 'push'} onClick={() => setActiveTab('push')} />
            <AdminNavItem icon="📊" text="Analytics" active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')} />
          </nav>
          
          <div className="mt-8 pt-8 border-t border-gray-800">
            <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-white">
              ← Back to Site
            </a>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-y-auto max-h-[calc(100vh-73px)]">
          {loading && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-900 rounded-lg p-6 text-white">Loading...</div>
            </div>
          )}

          {/* BROADCAST OS / NETWORK COMMAND TAB */}
          {activeTab === 'broadcast' && (
            <BroadcastOS authHeader={authHeader} />
          )}

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                <StatCard title="PPV Shows" value={ppvShows.length} icon="🎬" />
                <StatCard title="TV Shows" value={tvShows.length} icon="📺" />
                <StatCard title="Movies" value={movies.length} icon="🎥" />
                <StatCard title="Channels" value={channels.length} icon="📡" />
                <StatCard title="Subscribers" value={subscribers.total} icon="👥" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <QuickAction icon="📺" text="Add TV Show" onClick={() => setShowModal('tvshow')} />
                    <QuickAction icon="🎥" text="Add Movie" onClick={() => setShowModal('movie')} />
                    <QuickAction icon="📅" text="Add Schedule" onClick={() => { setActiveTab('schedule'); }} />
                    <QuickAction icon="📡" text="Add Channel" onClick={() => setShowModal('channel')} />
                  </div>
                </div>
                
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <h3 className="text-lg font-bold text-white mb-4">Scheduled Programs</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {schedules.slice(0, 5).map(s => (
                      <div key={s.id} className="flex items-center justify-between bg-gray-800 rounded p-2">
                        <div>
                          <p className="text-white text-sm font-medium">{s.title}</p>
                          <p className="text-gray-500 text-xs">{new Date(s.start_time).toLocaleString()}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${s.is_live ? 'bg-red-600' : 'bg-gray-600'}`}>
                          {s.content_type}
                        </span>
                      </div>
                    ))}
                    {schedules.length === 0 && <p className="text-gray-500 text-sm">No schedules yet</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PPV SHOWS TAB */}
          {activeTab === 'ppv' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">PPV Shows</h2>
                <button 
                  onClick={() => setShowModal('ppv')}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium"
                  data-testid="add-ppv-btn"
                >
                  + Add PPV Show
                </button>
              </div>
              
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Thumbnail</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Title</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Category</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Price</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ppvShows.map((show) => (
                      <tr key={show.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                        <td className="px-4 py-3">
                          <img src={show.thumbnail} alt="" className="w-16 h-20 object-cover rounded" />
                        </td>
                        <td className="px-4 py-3 text-white">{show.title}</td>
                        <td className="px-4 py-3 text-gray-400">{show.category}</td>
                        <td className="px-4 py-3 text-gray-400">${show.price_monthly}/mo</td>
                        <td className="px-4 py-3">
                          <button 
                            onClick={() => handleDeletePPV(show.id)}
                            className="text-red-500 hover:text-red-400 text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TV SHOWS TAB */}
          {activeTab === 'tvshows' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">TV Shows</h2>
                <button 
                  onClick={() => setShowModal('tvshow')}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium"
                  data-testid="add-tvshow-btn"
                >
                  + Add TV Show
                </button>
              </div>
              
              {tvShows.length === 0 ? (
                <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
                  <div className="text-5xl mb-4">📺</div>
                  <h3 className="text-white text-xl font-bold mb-2">No TV Shows Yet</h3>
                  <p className="text-gray-400 mb-4">Add your first TV show to get started</p>
                  <button 
                    onClick={() => setShowModal('tvshow')}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
                  >
                    Add TV Show
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {tvShows.map((show) => (
                    <div key={show.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                      <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {show.thumbnail ? (
                            <img src={show.thumbnail} alt="" className="w-16 h-24 object-cover rounded" />
                          ) : (
                            <div className="w-16 h-24 bg-gray-800 rounded flex items-center justify-center text-2xl">📺</div>
                          )}
                          <div>
                            <h3 className="text-white font-bold text-lg">{show.title}</h3>
                            <p className="text-gray-400 text-sm">{show.genre} • {show.year} • {show.rating}</p>
                            <p className="text-gray-500 text-sm">{show.seasons} season(s) • {show.episodes?.length || 0} episodes</p>
                            <span className={`inline-block mt-1 text-xs px-2 py-1 rounded ${
                              show.status === 'ongoing' ? 'bg-green-600' : show.status === 'ended' ? 'bg-gray-600' : 'bg-yellow-600'
                            }`}>
                              {show.status}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => { setSelectedShow(show); setShowModal('episode'); }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                          >
                            + Episode
                          </button>
                          <button 
                            onClick={() => handleDeleteTVShow(show.id)}
                            className="text-red-500 hover:text-red-400 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      {/* Episodes List */}
                      <div className="p-4 max-h-64 overflow-y-auto">
                        <h4 className="text-gray-400 text-sm font-medium mb-3">Episodes</h4>
                        {(!show.episodes || show.episodes.length === 0) ? (
                          <p className="text-gray-500 text-sm">No episodes added yet</p>
                        ) : (
                          <div className="space-y-2">
                            {show.episodes.map((ep) => (
                              <div key={ep.id} className="flex items-center justify-between bg-gray-800 rounded p-3">
                                <div className="flex items-center gap-3">
                                  {ep.thumbnail ? (
                                    <img src={ep.thumbnail} alt="" className="w-12 h-8 object-cover rounded" />
                                  ) : (
                                    <div className="w-12 h-8 bg-gray-700 rounded flex items-center justify-center text-xs">🎬</div>
                                  )}
                                  <div>
                                    <p className="text-white text-sm font-medium">S{ep.season}E{ep.episode_number}: {ep.title}</p>
                                    <p className="text-gray-500 text-xs">{ep.duration} min</p>
                                  </div>
                                </div>
                                <button 
                                  onClick={() => handleDeleteEpisode(show.id, ep.id)}
                                  className="text-red-500 hover:text-red-400 text-xs"
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MOVIES TAB */}
          {activeTab === 'movies' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Movies</h2>
                <button 
                  onClick={() => setShowModal('movie')}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium"
                  data-testid="add-movie-btn"
                >
                  + Add Movie
                </button>
              </div>
              
              {movies.length === 0 ? (
                <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
                  <div className="text-5xl mb-4">🎥</div>
                  <h3 className="text-white text-xl font-bold mb-2">No Movies Yet</h3>
                  <p className="text-gray-400 mb-4">Add your first movie to get started</p>
                  <button 
                    onClick={() => setShowModal('movie')}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
                  >
                    Add Movie
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {movies.map((movie) => (
                    <div key={movie.id} className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden group">
                      <div className="relative aspect-[2/3]">
                        {movie.poster || movie.thumbnail ? (
                          <img src={movie.poster || movie.thumbnail} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center text-4xl">🎥</div>
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button 
                            onClick={() => handleDeleteMovie(movie.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="text-white font-medium truncate">{movie.title}</h4>
                        <p className="text-gray-500 text-xs">{movie.year} • {movie.duration} min • {movie.genre}</p>
                        {movie.youtube_url && (
                          <span className="text-red-500 text-xs">YouTube</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CHANNELS TAB */}
          {activeTab === 'channels' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Channels</h2>
                <button 
                  onClick={() => setShowModal('channel')}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium"
                  data-testid="add-channel-btn"
                >
                  + Add Channel
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {channels.slice(0, 30).map((channel) => (
                  <div key={channel.id} className="bg-gray-900 rounded-lg border border-gray-800 p-4">
                    <div className="flex items-center gap-3 mb-3">
                      {channel.logo ? (
                        <img src={channel.logo} alt="" className="w-12 h-12 rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-800 rounded flex items-center justify-center text-lg">📺</div>
                      )}
                      <div className="flex-1">
                        <span className="text-xs text-gray-500">Ch. {channel.number}</span>
                        <h4 className="text-white font-medium">{channel.name}</h4>
                      </div>
                      <button 
                        onClick={() => { setSelectedChannel(channel); setActiveTab('schedule'); }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs"
                      >
                        Schedule
                      </button>
                    </div>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">{channel.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">{channel.category_id}</span>
                      <button 
                        onClick={() => handleDeleteChannel(channel.id)}
                        className="text-red-500 hover:text-red-400 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SCHEDULE TAB */}
          {activeTab === 'schedule' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">Schedule Management</h2>
                  {selectedChannel && (
                    <p className="text-gray-400">Managing schedule for: <span className="text-red-500">{selectedChannel.name}</span></p>
                  )}
                </div>
                <button 
                  onClick={() => setShowModal('schedule')}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium"
                  data-testid="add-schedule-btn"
                >
                  + Add Schedule Entry
                </button>
              </div>
              
              {/* Channel Selector */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 mb-6">
                <h3 className="text-white font-medium mb-3">Select Channel</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedChannel(null)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      !selectedChannel ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    All Channels
                  </button>
                  {channels.slice(0, 15).map(ch => (
                    <button
                      key={ch.id}
                      onClick={() => setSelectedChannel(ch)}
                      className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        selectedChannel?.id === ch.id ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {ch.number}. {ch.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Schedule Grid */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="grid grid-cols-6 bg-gray-800 text-gray-400 text-sm font-medium">
                  <div className="px-4 py-3">Channel</div>
                  <div className="px-4 py-3">Title</div>
                  <div className="px-4 py-3">Type</div>
                  <div className="px-4 py-3">Start Time</div>
                  <div className="px-4 py-3">Duration</div>
                  <div className="px-4 py-3">Actions</div>
                </div>
                
                {(selectedChannel ? getChannelSchedules(selectedChannel.id) : schedules).length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="text-4xl mb-3">📅</div>
                    <h3 className="text-white font-bold mb-2">No Schedule Entries</h3>
                    <p className="text-gray-400 mb-4">Add schedule entries to program your channels</p>
                    <button 
                      onClick={() => setShowModal('schedule')}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                    >
                      Add First Entry
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-800">
                    {(selectedChannel ? getChannelSchedules(selectedChannel.id) : schedules).map((entry) => {
                      const channel = channels.find(c => c.id === entry.channel_id);
                      return (
                        <div key={entry.id} className="grid grid-cols-6 hover:bg-gray-800/50">
                          <div className="px-4 py-3 flex items-center gap-2">
                            <span className="text-gray-500 text-xs">{channel?.number || '-'}</span>
                            <span className="text-white text-sm truncate">{channel?.name || entry.channel_id}</span>
                          </div>
                          <div className="px-4 py-3">
                            <p className="text-white text-sm font-medium truncate">{entry.title}</p>
                            {entry.description && <p className="text-gray-500 text-xs truncate">{entry.description}</p>}
                          </div>
                          <div className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded ${
                              entry.content_type === 'live' ? 'bg-red-600' : 
                              entry.content_type === 'movie' ? 'bg-purple-600' : 
                              entry.content_type === 'episode' ? 'bg-blue-600' : 'bg-gray-600'
                            }`}>
                              {entry.content_type}
                            </span>
                            {entry.is_live && <span className="ml-1 text-xs text-red-500">LIVE</span>}
                          </div>
                          <div className="px-4 py-3 text-gray-400 text-sm">
                            {new Date(entry.start_time).toLocaleDateString()}<br/>
                            <span className="text-gray-500 text-xs">{new Date(entry.start_time).toLocaleTimeString()}</span>
                          </div>
                          <div className="px-4 py-3 text-gray-400 text-sm">
                            {entry.duration} min
                            {entry.repeat !== 'none' && (
                              <span className="ml-1 text-xs text-yellow-500">({entry.repeat})</span>
                            )}
                          </div>
                          <div className="px-4 py-3">
                            <button 
                              onClick={() => handleDeleteSchedule(entry.id)}
                              className="text-red-500 hover:text-red-400 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CATEGORIES TAB */}
          {activeTab === 'categories' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Categories</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {categories.map((cat) => (
                  <div key={cat.id} className="bg-gray-900 rounded-lg border border-gray-800 p-4 text-center hover:border-red-600 transition-colors">
                    <span className="text-4xl mb-3 block">{cat.icon}</span>
                    <h4 className="text-white font-medium">{cat.name}</h4>
                    <p className="text-gray-500 text-sm">{cat.channel_count || 0} channels</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SUBSCRIBERS TAB */}
          {activeTab === 'subscribers' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Subscribers</h2>
                <button 
                  onClick={handleExportSubscribers}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                  data-testid="export-subscribers-btn"
                >
                  📥 Export CSV
                </button>
              </div>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
                  <p className="text-gray-400 text-sm">Total Subscribers</p>
                  <p className="text-3xl font-bold text-white">{subscribers.total}</p>
                </div>
                <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
                  <p className="text-gray-400 text-sm">New Releases</p>
                  <p className="text-2xl font-bold text-red-500">{subscribers.by_preference?.new_releases || 0}</p>
                </div>
                <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
                  <p className="text-gray-400 text-sm">PPV Events</p>
                  <p className="text-2xl font-bold text-yellow-500">{subscribers.by_preference?.ppv_events || 0}</p>
                </div>
                <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
                  <p className="text-gray-400 text-sm">Live Streams</p>
                  <p className="text-2xl font-bold text-blue-500">{subscribers.by_preference?.live_streams || 0}</p>
                </div>
                <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
                  <p className="text-gray-400 text-sm">Weekly Digest</p>
                  <p className="text-2xl font-bold text-green-500">{subscribers.by_preference?.weekly_digest || 0}</p>
                </div>
              </div>
              
              {/* Subscribers Table */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                {subscribers.subscribers?.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="text-5xl mb-4">👥</div>
                    <h3 className="text-white text-xl font-bold mb-2">No Subscribers Yet</h3>
                    <p className="text-gray-400">Subscribers will appear here when people sign up</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium">Email</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium">Name</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium">Preferences</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium">Subscribed</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.subscribers?.map((sub) => (
                        <tr key={sub.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                          <td className="px-4 py-3 text-white">{sub.email}</td>
                          <td className="px-4 py-3 text-gray-400">{sub.name || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {sub.preferences?.map(pref => (
                                <span key={pref} className={`text-xs px-2 py-1 rounded ${
                                  pref === 'new_releases' ? 'bg-red-600/20 text-red-400' :
                                  pref === 'ppv_events' ? 'bg-yellow-600/20 text-yellow-400' :
                                  pref === 'live_streams' ? 'bg-blue-600/20 text-blue-400' :
                                  'bg-green-600/20 text-green-400'
                                }`}>
                                  {pref.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-sm">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <button 
                              onClick={() => handleDeleteSubscriber(sub.id)}
                              className="text-red-500 hover:text-red-400 text-sm"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* EMAIL NOTIFICATIONS TAB */}
          {activeTab === 'email' && (
            <EmailNotificationsTab authHeader={authHeader} subscribers={subscribers} movies={movies} tvShows={tvShows} />
          )}

          {/* YOUTUBE IMPORT TAB */}
          {activeTab === 'youtube' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">YouTube Channel Import</h2>
              <YouTubeImporter 
                authHeader={authHeader}
                onImportComplete={() => fetchData()}
              />
            </div>
          )}

          {/* BULK UPLOAD TAB */}
          {activeTab === 'bulkupload' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Bulk Episode Upload</h2>
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Select TV Show</label>
                <select
                  value={selectedShow?.id || ''}
                  onChange={(e) => {
                    const show = tvShows.find(s => s.id === e.target.value);
                    setSelectedShow(show);
                  }}
                  className="w-full md:w-1/2 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
                >
                  <option value="">-- Select a TV Show --</option>
                  {tvShows.map(show => (
                    <option key={show.id} value={show.id}>{show.title}</option>
                  ))}
                </select>
              </div>
              {selectedShow ? (
                <BulkEpisodeUpload
                  showId={selectedShow.id}
                  showTitle={selectedShow.title}
                  authHeader={authHeader}
                  onUploadComplete={() => fetchData()}
                />
              ) : (
                <div className="bg-gray-800/50 rounded-lg p-8 text-center">
                  <p className="text-gray-400">Select a TV show above to upload episodes</p>
                </div>
              )}
            </div>
          )}

          {/* TRANSCODING TAB */}
          {activeTab === 'transcoding' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Video Transcoding</h2>
              <TranscodingManager
                authHeader={authHeader}
                onTranscodeComplete={() => fetchData()}
              />
            </div>
          )}

          {/* PAYMENTS TAB */}
          {activeTab === 'payments' && (
            <PaymentsAdminTab authHeader={authHeader} />
          )}

          {/* PUSH NOTIFICATIONS TAB */}
          {activeTab === 'push' && (
            <PushNotificationsAdminTab authHeader={authHeader} />
          )}

          {/* ANALYTICS TAB */}
          {activeTab === 'analytics' && (
            <AnalyticsDashboard authHeader={authHeader} />
          )}
        </main>
      </div>

      {/* MODALS */}
      {showModal === 'ppv' && (
        <Modal title="Add PPV Show" onClose={() => setShowModal(null)}>
          <PPVForm onSubmit={handleCreatePPV} onCancel={() => setShowModal(null)} />
        </Modal>
      )}

      {showModal === 'channel' && (
        <Modal title="Add Channel" onClose={() => setShowModal(null)}>
          <ChannelForm categories={categories} onSubmit={handleCreateChannel} onCancel={() => setShowModal(null)} />
        </Modal>
      )}

      {showModal === 'tvshow' && (
        <Modal title="Add TV Show" onClose={() => setShowModal(null)}>
          <TVShowForm onSubmit={handleCreateTVShow} onCancel={() => setShowModal(null)} />
        </Modal>
      )}

      {showModal === 'movie' && (
        <Modal title="Add Movie" onClose={() => setShowModal(null)}>
          <MovieForm onSubmit={handleCreateMovie} onCancel={() => setShowModal(null)} />
        </Modal>
      )}

      {showModal === 'episode' && selectedShow && (
        <Modal title={`Add Episode to "${selectedShow.title}"`} onClose={() => { setShowModal(null); setSelectedShow(null); }}>
          <EpisodeForm 
            showId={selectedShow.id} 
            onSubmit={(data) => handleAddEpisode(selectedShow.id, data)} 
            onCancel={() => { setShowModal(null); setSelectedShow(null); }} 
          />
        </Modal>
      )}

      {showModal === 'schedule' && (
        <Modal title="Add Schedule Entry" onClose={() => setShowModal(null)}>
          <ScheduleForm 
            channels={channels}
            movies={movies}
            tvShows={tvShows}
            defaultChannel={selectedChannel}
            onSubmit={handleCreateSchedule} 
            onCancel={() => setShowModal(null)} 
          />
        </Modal>
      )}
    </div>
  );
};

const AdminNavItem = ({ icon, text, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`admin-nav-${text.toLowerCase().replace(/\s+/g, '-')}`}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
      active ? 'bg-red-600 text-white' : 'text-gray-400 hover:bg-gray-800'
    }`}
  >
    <span>{icon}</span>
    <span>{text}</span>
  </button>
);

// =============================================================================
// PAYMENTS ADMIN TAB COMPONENT
// =============================================================================

const PaymentsAdminTab = ({ authHeader }) => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({ total: 0, successful: 0, total_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [priceModal, setPriceModal] = useState(null);
  const [newPrice, setNewPrice] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API}/admin/payments/transactions`, {
        headers: fetchAuthHeaders,
      });
      const data = await response.json();
      setTransactions(data.transactions || []);
      setStats({
        total: data.total || 0,
        successful: data.successful || 0,
        total_revenue: data.total_revenue || 0,
      });
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrice = async () => {
    if (!priceModal || !newPrice) return;
    
    try {
      await fetch(`${API}/admin/content/${priceModal.id}/set-price?price=${newPrice}&content_type=${priceModal.type}`, {
        method: 'POST',
        headers: fetchAuthHeaders,
      });
      setPriceModal(null);
      setNewPrice('');
      alert('Price updated successfully');
    } catch (error) {
      console.error('Error setting price:', error);
      alert('Failed to set price');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Payments & Revenue</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <p className="text-gray-400 text-sm">Total Transactions</p>
          <p className="text-3xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <p className="text-gray-400 text-sm">Successful Payments</p>
          <p className="text-3xl font-bold text-green-500">{stats.successful}</p>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <p className="text-gray-400 text-sm">Total Revenue</p>
          <p className="text-3xl font-bold text-yellow-500">${stats.total_revenue.toFixed(2)}</p>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-6">
        <h3 className="text-lg font-bold text-white mb-4">Subscription Plans</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-white font-medium">Basic Monthly</p>
            <p className="text-2xl font-bold text-red-500">$9.99</p>
          </div>
          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-white font-medium">Basic Yearly</p>
            <p className="text-2xl font-bold text-red-500">$99.99</p>
          </div>
          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-white font-medium">Premium Monthly</p>
            <p className="text-2xl font-bold text-yellow-500">$19.99</p>
          </div>
          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-white font-medium">Premium Yearly</p>
            <p className="text-2xl font-bold text-yellow-500">$199.99</p>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-bold text-white">Recent Transactions</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No transactions yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {transactions.slice(0, 10).map((tx) => (
                  <tr key={tx.id}>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        tx.type === 'subscription' ? 'bg-purple-500/20 text-purple-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {tx.type === 'subscription' ? 'Subscription' : 'PPV'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white">{tx.content_title || tx.plan_name || '-'}</td>
                    <td className="px-4 py-3 text-white">${tx.amount?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        tx.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        tx.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {tx.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// PUSH NOTIFICATIONS ADMIN TAB COMPONENT
// =============================================================================

const PushNotificationsAdminTab = ({ authHeader }) => {
  const [stats, setStats] = useState({ total_subscribers: 0, total_notifications_sent: 0, recent_notifications: [] });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState({ title: '', body: '', url: '' });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/push/stats`, {
        headers: fetchAuthHeaders,
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching push stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async () => {
    if (!notification.title || !notification.body) {
      alert('Please enter a title and body');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API}/admin/push/send`, {
        method: 'POST',
        headers: {
          ...fetchAuthHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notification),
      });
      const data = await response.json();
      alert(`Notification sent to ${data.sent_to} subscribers!`);
      setNotification({ title: '', body: '', url: '' });
      fetchStats();
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const sendNewContentNotification = async (contentId, contentType) => {
    setSending(true);
    try {
      const response = await fetch(`${API}/admin/push/new-content?content_id=${contentId}&content_type=${contentType}`, {
        method: 'POST',
        headers: fetchAuthHeaders,
      });
      const data = await response.json();
      alert(`Notification sent to ${data.sent_to} subscribers about "${data.content}"!`);
      fetchStats();
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Push Notifications</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <p className="text-gray-400 text-sm">Active Subscribers</p>
          <p className="text-3xl font-bold text-white">{stats.total_subscribers}</p>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <p className="text-gray-400 text-sm">Notifications Sent</p>
          <p className="text-3xl font-bold text-green-500">{stats.total_notifications_sent}</p>
        </div>
      </div>

      {/* Send Custom Notification */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-6">
        <h3 className="text-lg font-bold text-white mb-4">Send Custom Notification</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1">Title</label>
            <input
              type="text"
              value={notification.title}
              onChange={(e) => setNotification({ ...notification, title: e.target.value })}
              placeholder="Notification title"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-1">Body</label>
            <textarea
              value={notification.body}
              onChange={(e) => setNotification({ ...notification, body: e.target.value })}
              placeholder="Notification message"
              rows={3}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-1">URL (optional)</label>
            <input
              type="text"
              value={notification.url}
              onChange={(e) => setNotification({ ...notification, url: e.target.value })}
              placeholder="/library or https://..."
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
            />
          </div>
          <button
            onClick={sendNotification}
            disabled={sending || !notification.title || !notification.body}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-medium"
          >
            {sending ? 'Sending...' : 'Send Notification'}
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-6">
        <h3 className="text-lg font-bold text-white mb-4">Quick Notifications</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setNotification({
              title: '🎬 New Content Available!',
              body: 'Check out the latest movies and shows on CBXTV',
              url: '/library'
            })}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            New Content Template
          </button>
          <button
            onClick={() => setNotification({
              title: '🔴 Live Now!',
              body: 'A live stream just started. Tune in now!',
              url: '/live-tv'
            })}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            Live Stream Template
          </button>
          <button
            onClick={() => setNotification({
              title: '⏰ PPV Event Reminder',
              body: 'Don\'t miss the upcoming PPV event!',
              url: '/'
            })}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            PPV Reminder Template
          </button>
        </div>
      </div>

      {/* Recent Notifications */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-bold text-white">Recent Notifications</h3>
        </div>
        {!stats.recent_notifications || stats.recent_notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No notifications sent yet</div>
        ) : (
          <div className="divide-y divide-gray-700">
            {stats.recent_notifications.map((notif, index) => (
              <div key={index} className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{notif.title}</p>
                    <p className="text-gray-400 text-sm">{notif.body}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500 text-xs">{new Date(notif.sent_at).toLocaleString()}</p>
                    <p className="text-green-400 text-sm">{notif.recipients_count} recipients</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// EMAIL NOTIFICATIONS TAB COMPONENT
// =============================================================================

const EmailNotificationsTab = ({ authHeader, subscribers, movies, tvShows }) => {
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [notification, setNotification] = useState({
    subject: '',
    content_type: 'Movie',
    title: '',
    description: '',
    image_url: '',
    watch_url: '',
    preference_filter: 'new_releases'
  });
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    fetchEmailStatus();
  }, []);

  const fetchEmailStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/email/status`, authHeader);
      setEmailStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch email status:', error);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) return alert('Please enter an email address');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/email/test?test_email=${encodeURIComponent(testEmail)}`, null, authHeader);
      alert(`✅ Test email sent successfully to ${testEmail}`);
      setSendResult(res.data);
    } catch (error) {
      alert(`❌ Failed to send test email: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notification.subject || !notification.title) {
      return alert('Please fill in Subject and Title');
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        subject: notification.subject,
        content_type: notification.content_type,
        title: notification.title,
        description: notification.description,
        image_url: notification.image_url,
        watch_url: notification.watch_url,
        preference_filter: notification.preference_filter
      });
      const res = await axios.post(`${API}/admin/email/send-notification?${params}`, null, authHeader);
      setSendResult(res.data);
      alert(`✅ Email sent to ${res.data.sent} subscribers!`);
    } catch (error) {
      alert(`❌ Failed to send notification: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContent = (content, type) => {
    setNotification(prev => ({
      ...prev,
      content_type: type,
      title: content.title,
      description: content.description || '',
      image_url: content.poster || content.thumbnail || '',
      watch_url: `https://cbxtv.com/library`,
      subject: `🎬 New ${type} on CBXTV: ${content.title}`
    }));
  };

  return (
    <div data-testid="email-notifications-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">📧 Email Notifications</h2>
        {emailStatus && (
          <span className={`px-3 py-1 rounded-full text-sm ${emailStatus.configured ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
            {emailStatus.configured ? '✓ Email Service Active' : '✗ Not Configured'}
          </span>
        )}
      </div>

      {/* Test Email Section */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
        <h3 className="text-lg font-bold text-white mb-4">🧪 Send Test Email</h3>
        <p className="text-gray-400 text-sm mb-4">Verify your email configuration by sending a test email.</p>
        <div className="flex gap-4">
          <input
            type="email"
            placeholder="Enter test email address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
            data-testid="test-email-input"
          />
          <button
            onClick={handleSendTestEmail}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-medium"
            data-testid="send-test-email-btn"
          >
            {loading ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>
      </div>

      {/* Send Notification Section */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
        <h3 className="text-lg font-bold text-white mb-4">📣 Send New Content Notification</h3>
        
        {/* Quick Select from Content */}
        <div className="mb-6">
          <p className="text-gray-400 text-sm mb-3">Quick select from your content:</p>
          <div className="flex flex-wrap gap-2">
            {movies.slice(0, 5).map(movie => (
              <button
                key={movie.id}
                onClick={() => handleSelectContent(movie, 'Movie')}
                className="bg-gray-800 hover:bg-gray-700 text-white text-sm px-3 py-1 rounded-lg transition-colors"
              >
                🎥 {movie.title}
              </button>
            ))}
            {tvShows.slice(0, 5).map(show => (
              <button
                key={show.id}
                onClick={() => handleSelectContent(show, 'TV Show')}
                className="bg-gray-800 hover:bg-gray-700 text-white text-sm px-3 py-1 rounded-lg transition-colors"
              >
                📺 {show.title}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-gray-400 text-sm mb-2">Email Subject *</label>
            <input
              type="text"
              placeholder="🎬 New Release on CBXTV!"
              value={notification.subject}
              onChange={(e) => setNotification(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
              data-testid="notification-subject-input"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Content Type</label>
            <select
              value={notification.content_type}
              onChange={(e) => setNotification(prev => ({ ...prev, content_type: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
            >
              <option value="Movie">Movie</option>
              <option value="TV Show">TV Show</option>
              <option value="PPV Event">PPV Event</option>
              <option value="Live Stream">Live Stream</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Content Title *</label>
            <input
              type="text"
              placeholder="The Amazing Movie"
              value={notification.title}
              onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
              data-testid="notification-title-input"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Target Audience</label>
            <select
              value={notification.preference_filter}
              onChange={(e) => setNotification(prev => ({ ...prev, preference_filter: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
              data-testid="notification-audience-select"
            >
              <option value="new_releases">New Releases Subscribers ({subscribers.by_preference?.new_releases || 0})</option>
              <option value="ppv_events">PPV Events Subscribers ({subscribers.by_preference?.ppv_events || 0})</option>
              <option value="live_streams">Live Streams Subscribers ({subscribers.by_preference?.live_streams || 0})</option>
              <option value="weekly_digest">Weekly Digest Subscribers ({subscribers.by_preference?.weekly_digest || 0})</option>
              <option value="">All Active Subscribers ({subscribers.total})</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-gray-400 text-sm mb-2">Description</label>
            <textarea
              placeholder="Check out the latest content on CBXTV..."
              value={notification.description}
              onChange={(e) => setNotification(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none h-24 resize-none"
              data-testid="notification-description-input"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Image URL (optional)</label>
            <input
              type="url"
              placeholder="https://..."
              value={notification.image_url}
              onChange={(e) => setNotification(prev => ({ ...prev, image_url: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Watch URL</label>
            <input
              type="url"
              placeholder="https://cbxtv.com/library"
              value={notification.watch_url}
              onChange={(e) => setNotification(prev => ({ ...prev, watch_url: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
            />
          </div>
        </div>

        <button
          onClick={handleSendNotification}
          disabled={loading || !notification.subject || !notification.title}
          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium text-lg"
          data-testid="send-notification-btn"
        >
          {loading ? 'Sending...' : '📧 Send Notification to Subscribers'}
        </button>
      </div>

      {/* Send Result */}
      {sendResult && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <h3 className="text-lg font-bold text-white mb-4">📊 Last Send Result</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total Subscribers</p>
              <p className="text-2xl font-bold text-white">{sendResult.total_subscribers || '-'}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Emails Sent</p>
              <p className="text-2xl font-bold text-green-400">{sendResult.sent || '-'}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Failed</p>
              <p className="text-2xl font-bold text-red-400">{sendResult.failed || 0}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Email ID</p>
              <p className="text-sm text-gray-300 truncate">{sendResult.email_id || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon }) => (
  <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
    <div className="flex items-center justify-between mb-2">
      <span className="text-3xl">{icon}</span>
    </div>
    <h3 className="text-3xl font-bold text-white">{value}</h3>
    <p className="text-gray-400">{title}</p>
  </div>
);

const QuickAction = ({ icon, text, onClick }) => (
  <button
    onClick={onClick}
    className="bg-gray-800 hover:bg-gray-700 rounded-lg p-4 text-center transition-colors"
  >
    <span className="text-2xl block mb-2">{icon}</span>
    <span className="text-white text-sm">{text}</span>
  </button>
);

// =============================================================================
// FILE UPLOAD COMPONENT
// =============================================================================

const FileUpload = ({ type, label, accept, onUpload, currentUrl, maxSize }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentUrl || '');
  const fileInputRef = useRef(null);

  const adminToken = localStorage.getItem('adminToken');

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size
    const maxBytes = maxSize || (type === 'video' ? 500 * 1024 * 1024 : 10 * 1024 * 1024);
    if (file.size > maxBytes) {
      setError(`File too large. Max size: ${Math.round(maxBytes / (1024 * 1024))}MB`);
      return;
    }

    setError('');
    setUploading(true);
    setProgress(0);

    // Create preview for images
    if (type !== 'video' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = type === 'video' ? '/upload/video' : 
                      type === 'thumbnail' ? '/upload/thumbnail' : '/upload/image';

      const uploadHeaders = adminToken
        ? { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' }
        : { 'Content-Type': 'multipart/form-data' };
      const uploadAuth = adminToken
        ? undefined
        : { username: localStorage.getItem('adminUser') || 'Incognitox', password: '5683Mask' };

      const response = await axios.post(`${API}${endpoint}`, formData, {
        ...(uploadAuth ? { auth: uploadAuth } : {}),
        headers: uploadHeaders,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      });

      if (response.data.success) {
        const fullUrl = `${BACKEND_URL}${response.data.url}`;
        setPreview(fullUrl);
        onUpload && onUpload(fullUrl);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      setPreview(currentUrl || '');
    }

    setUploading(false);
  };

  const handleUrlInput = (url) => {
    setPreview(url);
    onUpload && onUpload(url);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm text-gray-400 mb-1">{label}</label>
      
      {/* Preview Area */}
      {preview && type !== 'video' && (
        <div className="relative w-full h-32 bg-gray-800 rounded-lg overflow-hidden mb-2">
          <img src={preview} alt="Preview" className="w-full h-full object-contain" />
          <button
            type="button"
            onClick={() => { setPreview(''); onUpload && onUpload(''); }}
            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 rounded-full p-1"
          >
            <X size={14} className="text-white" />
          </button>
        </div>
      )}

      {preview && type === 'video' && (
        <div className="relative w-full bg-gray-800 rounded-lg overflow-hidden mb-2">
          <video src={preview} controls className="w-full max-h-48" />
          <button
            type="button"
            onClick={() => { setPreview(''); onUpload && onUpload(''); }}
            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 rounded-full p-1"
          >
            <X size={14} className="text-white" />
          </button>
        </div>
      )}

      {/* Upload Button & URL Input */}
      <div className="flex gap-2">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept={accept}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-colors ${
            uploading 
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {uploading ? `${progress}%` : '📤 Upload'}
        </button>
        <input
          type="url"
          value={preview}
          onChange={(e) => handleUrlInput(e.target.value)}
          placeholder="Or paste URL..."
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-red-600 focus:outline-none"
        />
      </div>

      {/* Progress Bar */}
      {uploading && (
        <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-blue-600 h-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-red-500 text-xs">{error}</p>
      )}
    </div>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
    <div className="bg-gray-900 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <h3 className="text-lg font-bold text-white">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={24} />
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

const PPVForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail: '',
    logo: '',
    price_monthly: 9.99,
    price_yearly: 50.00,
    category: 'exclusive',
    youtube_url: '',
    video_url: '',
    premiere_date: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          required
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          rows={3}
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Thumbnail URL</label>
        <input
          type="url"
          value={formData.thumbnail}
          onChange={(e) => setFormData({...formData, thumbnail: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">YouTube URL</label>
        <input
          type="url"
          value={formData.youtube_url}
          onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="https://youtube.com/watch?v=..."
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Monthly Price</label>
          <input
            type="number"
            step="0.01"
            value={formData.price_monthly}
            onChange={(e) => setFormData({...formData, price_monthly: parseFloat(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Yearly Price</label>
          <input
            type="number"
            step="0.01"
            value={formData.price_yearly}
            onChange={(e) => setFormData({...formData, price_yearly: parseFloat(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Category</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({...formData, category: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        >
          <option value="exclusive">Exclusive</option>
          <option value="podcast">Podcast</option>
          <option value="series">Series</option>
          <option value="documentary">Documentary</option>
        </select>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Premiere Date</label>
        <input
          type="date"
          value={formData.premiere_date}
          onChange={(e) => setFormData({...formData, premiere_date: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        />
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg">
          Create Show
        </button>
      </div>
    </form>
  );
};

const ChannelForm = ({ categories, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    number: 200,
    name: '',
    slug: '',
    logo: '',
    description: '',
    category_id: 'entertainment',
    stream_url: '',
    youtube_channel_id: '',
    pluto_link: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const slug = formData.name.toLowerCase().replace(/\s+/g, '-');
    onSubmit({...formData, slug});
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Channel Number *</label>
          <input
            type="number"
            value={formData.number}
            onChange={(e) => setFormData({...formData, number: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Category</label>
          <select
            value={formData.category_id}
            onChange={(e) => setFormData({...formData, category_id: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Channel Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          required
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          rows={2}
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Logo URL</label>
        <input
          type="url"
          value={formData.logo}
          onChange={(e) => setFormData({...formData, logo: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">YouTube Channel ID</label>
        <input
          type="text"
          value={formData.youtube_channel_id}
          onChange={(e) => setFormData({...formData, youtube_channel_id: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="UCxxxxxxx"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">YouTube Live Stream URL</label>
        <input
          type="url"
          value={formData.stream_url}
          onChange={(e) => setFormData({...formData, stream_url: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
        />
        <p className="text-gray-500 text-xs mt-1">Paste any YouTube video or live stream URL</p>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Pluto.tv Link</label>
        <input
          type="url"
          value={formData.pluto_link}
          onChange={(e) => setFormData({...formData, pluto_link: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="https://pluto.tv/live-tv/..."
        />
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg">
          Create Channel
        </button>
      </div>
    </form>
  );
};

// =============================================================================
// TV SHOW FORM
// =============================================================================

const TVShowForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail: '',
    poster: '',
    genre: 'Drama',
    rating: 'TV-14',
    year: 2024,
    seasons: 1,
    status: 'ongoing'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="tvshow-form">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none"
          required
          data-testid="tvshow-title"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none"
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Genre</label>
          <select
            value={formData.genre}
            onChange={(e) => setFormData({...formData, genre: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="Drama">Drama</option>
            <option value="Comedy">Comedy</option>
            <option value="Action">Action</option>
            <option value="Horror">Horror</option>
            <option value="Thriller">Thriller</option>
            <option value="Sci-Fi">Sci-Fi</option>
            <option value="Reality">Reality</option>
            <option value="Documentary">Documentary</option>
            <option value="Romance">Romance</option>
            <option value="Crime">Crime</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Rating</label>
          <select
            value={formData.rating}
            onChange={(e) => setFormData({...formData, rating: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="TV-G">TV-G</option>
            <option value="TV-PG">TV-PG</option>
            <option value="TV-14">TV-14</option>
            <option value="TV-MA">TV-MA</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Year</label>
          <input
            type="number"
            value={formData.year}
            onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Seasons</label>
          <input
            type="number"
            min="1"
            value={formData.seasons}
            onChange={(e) => setFormData({...formData, seasons: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="ongoing">Ongoing</option>
            <option value="ended">Ended</option>
            <option value="upcoming">Upcoming</option>
          </select>
        </div>
      </div>
      <FileUpload
        type="thumbnail"
        label="Thumbnail Image"
        accept="image/*"
        currentUrl={formData.thumbnail}
        onUpload={(url) => setFormData({...formData, thumbnail: url})}
      />
      <FileUpload
        type="image"
        label="Poster Image"
        accept="image/*"
        currentUrl={formData.poster}
        onUpload={(url) => setFormData({...formData, poster: url})}
      />
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg" data-testid="tvshow-submit">
          Create TV Show
        </button>
      </div>
    </form>
  );
};

// =============================================================================
// EPISODE FORM
// =============================================================================

const EpisodeForm = ({ showId, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    season: 1,
    episode_number: 1,
    thumbnail: '',
    video_url: '',
    youtube_url: '',
    duration: 30,
    air_date: '',
    rating: 'TV-14'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="episode-form">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Episode Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none"
          required
          data-testid="episode-title"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          rows={2}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Season</label>
          <input
            type="number"
            min="1"
            value={formData.season}
            onChange={(e) => setFormData({...formData, season: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Episode #</label>
          <input
            type="number"
            min="1"
            value={formData.episode_number}
            onChange={(e) => setFormData({...formData, episode_number: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Duration (min)</label>
          <input
            type="number"
            min="1"
            value={formData.duration}
            onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">YouTube URL</label>
        <input
          type="url"
          value={formData.youtube_url}
          onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="https://youtube.com/watch?v=..."
        />
      </div>
      <FileUpload
        type="video"
        label="Upload Video (or paste URL)"
        accept="video/mp4,video/webm,video/quicktime"
        currentUrl={formData.video_url}
        onUpload={(url) => setFormData({...formData, video_url: url})}
        maxSize={500 * 1024 * 1024}
      />
      <FileUpload
        type="thumbnail"
        label="Episode Thumbnail"
        accept="image/*"
        currentUrl={formData.thumbnail}
        onUpload={(url) => setFormData({...formData, thumbnail: url})}
      />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Air Date</label>
          <input
            type="date"
            value={formData.air_date}
            onChange={(e) => setFormData({...formData, air_date: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Rating</label>
          <select
            value={formData.rating}
            onChange={(e) => setFormData({...formData, rating: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="TV-G">TV-G</option>
            <option value="TV-PG">TV-PG</option>
            <option value="TV-14">TV-14</option>
            <option value="TV-MA">TV-MA</option>
          </select>
        </div>
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg" data-testid="episode-submit">
          Add Episode
        </button>
      </div>
    </form>
  );
};

// =============================================================================
// MOVIE FORM
// =============================================================================

const MovieForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail: '',
    poster: '',
    video_url: '',
    youtube_url: '',
    genre: 'Action',
    rating: 'PG-13',
    year: 2024,
    duration: 120,
    director: '',
    cast: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="movie-form">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none"
          required
          data-testid="movie-title"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          rows={2}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Genre</label>
          <select
            value={formData.genre}
            onChange={(e) => setFormData({...formData, genre: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="Action">Action</option>
            <option value="Comedy">Comedy</option>
            <option value="Drama">Drama</option>
            <option value="Horror">Horror</option>
            <option value="Thriller">Thriller</option>
            <option value="Sci-Fi">Sci-Fi</option>
            <option value="Romance">Romance</option>
            <option value="Documentary">Documentary</option>
            <option value="Animation">Animation</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Rating</label>
          <select
            value={formData.rating}
            onChange={(e) => setFormData({...formData, rating: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="G">G</option>
            <option value="PG">PG</option>
            <option value="PG-13">PG-13</option>
            <option value="R">R</option>
            <option value="NC-17">NC-17</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Year</label>
          <input
            type="number"
            value={formData.year}
            onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Duration (min)</label>
          <input
            type="number"
            value={formData.duration}
            onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Director</label>
        <input
          type="text"
          value={formData.director}
          onChange={(e) => setFormData({...formData, director: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Cast</label>
        <input
          type="text"
          value={formData.cast}
          onChange={(e) => setFormData({...formData, cast: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="Actor 1, Actor 2, ..."
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">YouTube URL</label>
        <input
          type="url"
          value={formData.youtube_url}
          onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="https://youtube.com/watch?v=..."
        />
      </div>
      <FileUpload
        type="video"
        label="Upload Movie Video (or paste URL)"
        accept="video/mp4,video/webm,video/quicktime"
        currentUrl={formData.video_url}
        onUpload={(url) => setFormData({...formData, video_url: url})}
        maxSize={500 * 1024 * 1024}
      />
      <FileUpload
        type="image"
        label="Movie Poster"
        accept="image/*"
        currentUrl={formData.poster}
        onUpload={(url) => setFormData({...formData, poster: url})}
      />
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg" data-testid="movie-submit">
          Add Movie
        </button>
      </div>
    </form>
  );
};

// =============================================================================
// SCHEDULE FORM
// =============================================================================

const ScheduleForm = ({ channels, movies, tvShows, defaultChannel, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    channel_id: defaultChannel?.id || '',
    content_type: 'custom',
    content_id: '',
    title: '',
    description: '',
    thumbnail: '',
    video_url: '',
    youtube_url: '',
    start_time: '',
    duration: 30,
    is_live: false,
    repeat: 'none'
  });

  // Set default date to today at next hour
  useEffect(() => {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setFormData(prev => ({ ...prev, start_time: localDateTime }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert local time to ISO
    const isoTime = new Date(formData.start_time).toISOString();
    onSubmit({ ...formData, start_time: isoTime });
  };

  // Auto-fill from content selection
  const handleContentSelect = (contentId, type) => {
    if (type === 'movie') {
      const movie = movies.find(m => m.id === contentId);
      if (movie) {
        setFormData(prev => ({
          ...prev,
          content_id: contentId,
          title: movie.title,
          description: movie.description,
          thumbnail: movie.poster || movie.thumbnail,
          video_url: movie.video_url,
          youtube_url: movie.youtube_url,
          duration: movie.duration
        }));
      }
    } else if (type === 'episode') {
      // Find episode from tvShows
      for (const show of tvShows) {
        const episode = show.episodes?.find(ep => ep.id === contentId);
        if (episode) {
          setFormData(prev => ({
            ...prev,
            content_id: contentId,
            title: `${show.title} - S${episode.season}E${episode.episode_number}: ${episode.title}`,
            description: episode.description,
            thumbnail: episode.thumbnail,
            video_url: episode.video_url,
            youtube_url: episode.youtube_url,
            duration: episode.duration
          }));
          break;
        }
      }
    }
  };

  // Get all episodes from all shows
  const allEpisodes = tvShows.flatMap(show => 
    (show.episodes || []).map(ep => ({
      ...ep,
      showTitle: show.title,
      displayName: `${show.title} - S${ep.season}E${ep.episode_number}: ${ep.title}`
    }))
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="schedule-form">
      {/* Channel Selection */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Channel *</label>
        <select
          value={formData.channel_id}
          onChange={(e) => setFormData({...formData, channel_id: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          required
          data-testid="schedule-channel"
        >
          <option value="">Select a channel...</option>
          {channels.map(ch => (
            <option key={ch.id} value={ch.id}>{ch.number}. {ch.name}</option>
          ))}
        </select>
      </div>

      {/* Content Type */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Content Type</label>
        <select
          value={formData.content_type}
          onChange={(e) => setFormData({...formData, content_type: e.target.value, content_id: ''})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        >
          <option value="custom">Custom Program</option>
          <option value="movie">Movie</option>
          <option value="episode">TV Episode</option>
          <option value="live">Live Stream</option>
        </select>
      </div>

      {/* Content Selection for Movie */}
      {formData.content_type === 'movie' && movies.length > 0 && (
        <div>
          <label className="block text-sm text-gray-400 mb-1">Select Movie</label>
          <select
            value={formData.content_id}
            onChange={(e) => handleContentSelect(e.target.value, 'movie')}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="">Select a movie...</option>
            {movies.map(m => (
              <option key={m.id} value={m.id}>{m.title} ({m.year})</option>
            ))}
          </select>
        </div>
      )}

      {/* Content Selection for Episode */}
      {formData.content_type === 'episode' && allEpisodes.length > 0 && (
        <div>
          <label className="block text-sm text-gray-400 mb-1">Select Episode</label>
          <select
            value={formData.content_id}
            onChange={(e) => handleContentSelect(e.target.value, 'episode')}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="">Select an episode...</option>
            {allEpisodes.map(ep => (
              <option key={ep.id} value={ep.id}>{ep.displayName}</option>
            ))}
          </select>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Program Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none"
          required
          data-testid="schedule-title"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          rows={2}
        />
      </div>

      {/* Start Time and Duration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Start Time *</label>
          <input
            type="datetime-local"
            value={formData.start_time}
            onChange={(e) => setFormData({...formData, start_time: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
            required
            data-testid="schedule-start-time"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Duration (min)</label>
          <input
            type="number"
            min="1"
            value={formData.duration}
            onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          />
        </div>
      </div>

      {/* YouTube URL */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">YouTube URL</label>
        <input
          type="url"
          value={formData.youtube_url}
          onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          placeholder="https://youtube.com/watch?v=..."
        />
      </div>

      {/* Video URL */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Video URL (direct)</label>
        <input
          type="url"
          value={formData.video_url}
          onChange={(e) => setFormData({...formData, video_url: e.target.value})}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        />
      </div>

      {/* Options Row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Repeat</label>
          <select
            value={formData.repeat}
            onChange={(e) => setFormData({...formData, repeat: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="none">No Repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>
        <div className="flex items-center pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_live}
              onChange={(e) => setFormData({...formData, is_live: e.target.checked})}
              className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-red-600 focus:ring-red-600"
            />
            <span className="text-white">Mark as Live</span>
          </label>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg" data-testid="schedule-submit">
          Add to Schedule
        </button>
      </div>
    </form>
  );
};

export default AdminDashboard;
export { Modal, FileUpload };
